"use client";
import { Modal } from "@/components/ui/Modal";
import { cdnCommonPath } from "@/constants/cdnUrls";
import { useModal } from "@/contexts/ModalContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useSmartBanner } from "@/hooks/useSmartBanner";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import Image from "next/image";
import { useCallback, useEffect, useRef } from "react";

export default function SmartBanner() {
  const { isVisible, handleClose } = useSmartBanner();
  const t = useTranslationDetector("common");
  const router = useRouter();
  const modal = useModal("download-app-banner");
  const isMobile = useIsMobile();
  // Handle browser continue button
  const handleBrowserContinue = useCallback(() => {
    modal.close();
    handleClose();
    // Navigate to current page (stays on browser)
    // You can change this to navigate to a specific route if needed
  }, [modal, handleClose]);

  // Handle app continue button
  const handleAppContinue = useCallback(() => {
    modal.close();
    handleClose();
    // Navigate to app download or deep link
    router.push(
      "https://turna.go.link?adj_t=1wcv0729&adj_engagement_type=fallback_click",
    );
  }, [modal, handleClose, router]);

  // Sync modal state with banner visibility
  useEffect(() => {
    if (isVisible && !modal.isOpen) {
      modal.open();
    } else if (!isVisible && modal.isOpen) {
      modal.close();
    }
  }, [isVisible, modal]);

  // Handle modal close via backdrop or X button - update smart banner state
  const prevModalOpen = useRef(modal.isOpen);
  useEffect(() => {
    // If modal was open and now closed, update banner state
    if (prevModalOpen.current && !modal.isOpen && isVisible) {
      handleClose();
    }
    prevModalOpen.current = modal.isOpen;
  }, [modal.isOpen, isVisible, handleClose]);

  function detectMobileBrowser() {
    if (typeof window === "undefined" || !isMobile || !window?.navigator)
      return {
        isSafari: false,
        isChrome: true,
        isIOS: false,
        isAndroid: false,
        userAgent: "",
      };

    const ua = navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(ua);
    const isAndroid = /android/.test(ua);

    return {
      isSafari: isIOS && /safari/.test(ua) && !/crios|chrome|fxios/.test(ua),
      isChrome:
        (/crios/.test(ua) && isIOS) ||
        (/chrome/.test(ua) && isAndroid && !/edg/.test(ua)),
      isIOS,
      isAndroid,
      userAgent: ua,
    };
  }

  // Kullanımı
  const browser = detectMobileBrowser();

  if (!isMobile) return null;
  return (
    <Modal
      id="download-app-banner"
      isBottomSheet={true}
      showCloseButton={false}
      closeOnBackdropClick={true}
      // title={t("downloadAppBannerTitle")}
      contentClassName="p-0"
    >
      <div className="flex flex-col gap-4 p-6">
        <div className="relative flex items-center justify-center text-center">
          <div className="font-semibold">{t("downloadAppBannerTitle")}</div>
          {/* <button
            className="bg-background-global absolute top-0 right-0 flex h-10 w-10 cursor-pointer items-center justify-center rounded-full text-[24px]"
            onClick={handleBrowserContinue}
          >
            <FontAwesomeIcon
              icon={byPrefixAndName.fak["x-mark-custom"]}
              className="text-foreground-muted text-lg"
            />
          </button> */}
        </div>

        <div className="flex flex-col gap-3">
          {/* Second Option: App Continue */}
          <div className="flex items-center justify-between">
            <div className="flex flex-1 items-center gap-3">
              <div className="flex-shrink-0">
                <Image
                  src={`${cdnCommonPath}turna-app-icon.png`}
                  alt="App Store"
                  width={48}
                  height={48}
                />
              </div>
              <span className="text-base font-medium text-gray-900">
                {t("turna")}
              </span>
            </div>
            <button
              onClick={handleAppContinue}
              className="w-30 items-center gap-2 rounded-full bg-[#007aff] px-5 py-2 text-center text-sm font-semibold text-white transition-colors"
            >
              {t("open")}
            </button>
          </div>

          {/* First Option: Browser Continue */}
          <div className="flex items-center justify-between transition-colors hover:bg-gray-50">
            <div className="flex flex-1 items-center gap-3">
              <div className="flex-shrink-0">
                <Image
                  src={`${cdnCommonPath}${browser.isSafari ? "safari" : "chrome"}.svg`}
                  alt="App Store"
                  className="border-primary-weak rounded-lg border"
                  width={48}
                  height={48}
                />
              </div>
              <span className="text-base font-medium text-gray-900">
                {t("browser")}
              </span>
            </div>
            <button
              onClick={handleBrowserContinue}
              className="bg-background-global text-foreground-muted w-30 items-center gap-2 rounded-full px-5 py-2 text-center text-sm font-semibold transition-colors"
            >
              {t("continue")}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
